import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type AssigneePickerAssignee, AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {useViewer} from '@github-ui/item-picker-assignee-relay/useViewer'
import {
  BulkIssueAssigneePicker as BulkIssueAssigneePickerTsq,
  type BulkIssueAssigneePickerProps as BulkIssueAssigneePickerTsqProps,
} from '@github-ui/item-picker-assignee-tsq/BulkIssueAssigneePicker'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type React from 'react'
import {useCallback, useMemo, useState} from 'react'
import {commitMutation, graphql, useRelayEnvironment} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'

import type {BulkIssuesAssigneePickerMutation} from './__generated__/BulkIssuesAssigneePickerMutation.graphql'
import {addAssigneesToStore, updateIssuesById} from './relay-assignee-interop'

type IssueUpdateArgs = {
  applyAssigneeIds: string[]
  removeAssigneeIds: string[]
}

type UpdateIssuesBulkInput = {
  ids: string[]
} & IssueUpdateArgs

export type BulkAssigneePickerProps = {
  owner: string
  repo: string
  name?: 'assignee' | 'author'
  readonly?: boolean
  anchorElement: (
    anchorProps: React.HTMLAttributes<HTMLElement> & {ref?: React.Ref<HTMLButtonElement | HTMLLIElement>},
    ref?: React.Ref<HTMLButtonElement | HTMLLIElement>,
  ) => React.ReactElement
  suggestions?: AssigneePickerAssignee[]
  maximumAssignees?: number
  insidePortal?: boolean
  nested?: boolean
  triggerOpen?: boolean
  assigneesAppliedToAll: AssigneePickerAssignee[]
  assigneesAppliedToSome: AssigneePickerAssignee[]
  existingIssueAssignees: Map<string, string[]>
  connectionIds: {[key: string]: string[]}
  issueNumbers: number[]
  repository?: {
    databaseId: number | null
    isPrivate: boolean
    isArchived: boolean
  }
  /** Optional externally managed ref to the anchor button for focus return after Copilot modal closes. */
  externalReturnFocusRef?: React.RefObject<HTMLElement | null>
} & SharedBulkActionsItemPickerProps

export function BulkIssuesAssigneePicker(props: BulkAssigneePickerProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_assignee_tsq_migration')
  return isTsqPickerEnabled ? (
    <BulkIssuesAssigneePickerTsqWithRelayWrapper {...props} />
  ) : (
    <BulkIssuesAssigneePickerRelay {...props} />
  )
}

function BulkIssuesAssigneePickerTsqWithRelayWrapper({
  connectionIds,
  existingIssueAssignees,
  issuesToActOn,
  ...props
}: BulkAssigneePickerProps) {
  const environment = useRelayEnvironment()
  const viewer = useViewer()

  const onMutate: BulkIssueAssigneePickerTsqProps['onMutate'] = useCallback(
    input => {
      environment.commitUpdate(store => {
        // Add assignees to store if they're being added and assignees data is provided
        if (input.applyAssigneeIds && input.applyAssigneeIds.length > 0 && input.assignees) {
          const applyIds = input.applyAssigneeIds // Extract for use in filter
          // Filter assignees to only those being added
          const assigneesToAdd = input.assignees.filter((assignee: {id: string}) => applyIds.includes(assignee.id))
          addAssigneesToStore(store, assigneesToAdd)
        }

        const inputArgs = {
          applyAssigneeIds: input.applyAssigneeIds || [],
          removeAssigneeIds: input.removeAssigneeIds || [],
        }
        updateIssuesById(store, issuesToActOn, existingIssueAssignees, connectionIds, inputArgs)
      })
    },
    [connectionIds, environment, existingIssueAssignees, issuesToActOn],
  )
  return (
    <BulkIssueAssigneePickerTsq
      {...props}
      issuesToActOn={issuesToActOn}
      existingIssueAssignees={existingIssueAssignees}
      connectionIds={connectionIds}
      viewer={viewer}
      onMutate={onMutate}
    />
  )
}

function commitUpdateIssueAssigneesBulkMutation({
  environment,
  optimisticUpdateIds,
  existingIssueAssignees,
  connectionIds,
  input: {ids, applyAssigneeIds, removeAssigneeIds},
  onCompleted,
  onError,
}: {
  environment: Environment
  connectionIds: {[key: string]: string[]}
  input: {ids: string[]} & IssueUpdateArgs
  optimisticUpdateIds: string[]
  existingIssueAssignees: Map<string, string[]>
  onCompleted?: (response: BulkIssuesAssigneePickerMutation['response']) => void
  onError?: (error: Error) => void
}) {
  const inputArgs = {applyAssigneeIds, removeAssigneeIds}
  const inputHash: UpdateIssuesBulkInput = {
    ids,
    ...inputArgs,
  }

  return commitMutation<BulkIssuesAssigneePickerMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation BulkIssuesAssigneePickerMutation($input: UpdateIssuesBulkInput!) @raw_response_type {
        updateIssuesBulk(input: $input) {
          jobId
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    updater: (store: RecordSourceProxy) =>
      updateIssuesById(store, optimisticUpdateIds, existingIssueAssignees, connectionIds, inputArgs),
    optimisticUpdater: (store: RecordSourceProxy) =>
      updateIssuesById(store, optimisticUpdateIds, existingIssueAssignees, connectionIds, inputArgs),
    onCompleted: response => onCompleted && onCompleted(response),
    onError: error => onError && onError(error),
  })
}

export function BulkIssuesAssigneePickerRelay({
  issuesToActOn,
  assigneesAppliedToAll,
  assigneesAppliedToSome,
  existingIssueAssignees,
  connectionIds,
  onError,
  onCompleted,
  repository,
  issueNumbers,
  externalReturnFocusRef,
  ...rest
}: BulkAssigneePickerProps) {
  const environment = useRelayEnvironment()
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)

  const assigneesIncludeCopilot = useMemo(() => {
    return assigneesAppliedToAll.some(assignee => assignee.isCopilot)
  }, [assigneesAppliedToAll])

  const canUseCopilotAssignment = !assigneesIncludeCopilot

  const issueNumbersWithoutCopilot = useMemo(() => {
    const copilotId = [...assigneesAppliedToAll, ...assigneesAppliedToSome].find(a => a.isCopilot)?.id

    if (!copilotId) return [...issueNumbers]

    return issuesToActOn.reduce((acc: number[], issueId, idx) => {
      const assignees = existingIssueAssignees.get(issueId) || []
      const hasCopilot = assignees.some(id => id === copilotId)
      const numberAtIndex = issueNumbers[idx]
      if (!hasCopilot && typeof numberAtIndex === 'number') acc.push(numberAtIndex)
      return acc
    }, [])
  }, [assigneesAppliedToAll, assigneesAppliedToSome, existingIssueAssignees, issueNumbers, issuesToActOn])

  const executeBulkAssigneeMutation = useCallback(
    (assigneesToProcess: AssigneePickerAssignee[]) => {
      const assigneesToAdd = assigneesToProcess.filter(a => !assigneesAppliedToAll.some(aa => aa.id === a.id))
      const assigneesToRemove = assigneesAppliedToAll.filter(a => !assigneesToProcess.some(aa => aa.id === a.id))

      if (!assigneesToAdd.length && !assigneesToRemove.length) {
        return
      }

      const args = {
        applyAssigneeIds: assigneesToAdd.map(l => l.id),
        removeAssigneeIds: assigneesToRemove.map(l => l.id),
      }

      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueAssignees,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      commitUpdateIssueAssigneesBulkMutation({
        ...mutationArgs,
        input: {
          ids: [...issuesToActOn],
          ...args,
        },
        onCompleted: response => {
          onCompleted?.(response.updateIssuesBulk?.jobId || undefined)
        },
      })
    },
    [assigneesAppliedToAll, connectionIds, environment, existingIssueAssignees, issuesToActOn, onCompleted, onError],
  )

  const handleCopilotModalOpen = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems (equivalent to Cancel)
      This is needed because when we do nothing with Copilot, it remains selected in the picker
      and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [])

  const onSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const copilotAssignee = selectedAssignees.find(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      if (copilotAssignee && canUseCopilotAssignment) {
        // open modal and process non-copilot changes in the background
        handleCopilotModalOpen()
        executeBulkAssigneeMutation(nonCopilotAssignees)
      } else {
        executeBulkAssigneeMutation(selectedAssignees)
      }
    },
    [executeBulkAssigneeMutation, canUseCopilotAssignment, handleCopilotModalOpen],
  )

  const showModal = canUseCopilotAssignment && copilotModalOpen

  const repositoryProps = useMemo(
    () => ({
      name: rest.repo,
      owner: {
        login: rest.owner,
      },
    }),
    [rest.repo, rest.owner],
  )

  return (
    <>
      <AssigneeRepositoryPicker
        key={`bulk-assignee-picker-${pickerInstanceKey}`}
        {...rest}
        responsiveOnNarrow
        assignees={assigneesAppliedToAll}
        assigneeTokens={[]}
        onSelectionChange={onSelectionChange}
        includeAssignableBots
        includeAuthorableBots={false}
      />
      {showModal && issueNumbersWithoutCopilot.length > 0 && (
        <CopilotAssignmentModal
          isOpen={copilotModalOpen}
          onClose={() => setCopilotModalOpen(false)}
          repository={repositoryProps}
          issueNumbers={issueNumbersWithoutCopilot}
          onAssignmentComplete={() => {
            setCopilotModalOpen(false)
            onCompleted?.()
          }}
          returnFocusRef={externalReturnFocusRef}
        />
      )}
    </>
  )
}

try{ BulkIssuesAssigneePicker.displayName ||= 'BulkIssuesAssigneePicker' } catch {}
try{ BulkIssuesAssigneePickerTsqWithRelayWrapper.displayName ||= 'BulkIssuesAssigneePickerTsqWithRelayWrapper' } catch {}
try{ BulkIssuesAssigneePickerRelay.displayName ||= 'BulkIssuesAssigneePickerRelay' } catch {}